import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useNavigate } from "react-router-dom";
import { Layout, Menu, Badge, Dropdown } from "antd";

import { flattenTree } from "utils/commonUtils";
import { SettingsM } from "./../../components/icons/setting_m";
import { Attendance } from "./../../components/icons/attendance";
import { Dashboard } from "./../../components/icons/dashboard";
import { Wellness } from "./../../components/icons/wellness";
import { Livestream } from "./../../components/icons/livestream";
import { Activity } from "./../../components/icons/activity";
import { Productivity } from "./../../components/icons/productivity";
import { Timeline } from "./../../components/icons/timeline";
import { Application } from "./../../components/icons/application";
import { Reports } from "./../../components/icons/reports";
import { Project } from "./../../components/icons/project";
import { Alerts } from "components/icons/alerts";
import { TbLiveView } from "react-icons/tb";
import {
  MdOutlineIntegrationInstructions,
  MdScreenshotMonitor,
} from "react-icons/md";
import { Realtime } from "components/icons/RealTime";
import { Analytics } from "components/icons/Analytics";
import { Leave } from "components/icons/leave";

import "./index.scss";
import {
  ACTIVITY,
  ALERTS,
  APPLICATION,
  ATTENDANCE,
  DASHBOARD,
  LIVESTREAM,
  ME,
  PRODUCTIVITY,
  REPORTS,
  SETTINGS,
  TIMELINE,
  WELLNESS,
  PROJECTS,
  FIELD,
  SCREENSHOT,
  NOTEBOOK,
  LEAVE_MANAGEMENT,
  DEVICE360,
  USB_DETECTION,
  GOALS,
  MANUALTIME,
  TIMESHEET,
  INTEGRATION_DASHBOARD,
} from "route/constant";
import { permissionsMap } from "constants/constants";
import { AuthContext } from "contexts/authContext";
import { AiOutlineUser } from "react-icons/ai";
import { GiNotebook } from "react-icons/gi";
import { getLogoUrl } from "utils/transport";
import { Goals } from "components/icons/Goals";
import { ManualTime } from "../../components/icons/manualTime";
import { LuArrowLeftToLine, LuArrowRightFromLine } from "react-icons/lu";
import { BsFillGridFill } from "react-icons/bs";
import { Timesheet } from "../../components/icons/timesheet";
import { useSelector } from "react-redux";
import { UsbDetection } from "components/icons/usbdetection";
import { DeviceInfoIcon } from "components/icons/DeviceInfoIcon";
import { useBranding } from "hooks/useBranding";
import { IoDiamondOutline } from "react-icons/io5";
import {
  openEnterpriseAppInNewTab,
  shouldShowV4Enterprise,
} from "utils/redirectUtils";
import Productivitysuite from "../../assets/images/productivity suite.svg";
import Projectsuite from "../../assets/images/project suite.svg";
import HRsuite from "../../assets/images/HR suite.svg";

const { Sider } = Layout;

const Sidebar = (props) => {
  const branding = useBranding();
  const logoRef = useRef(null);
  const { silderBtn } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [activeMenu, setActiveMenu] = useState(
    localStorage.getItem("activeMenu") || "productivity"
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const navigate = useNavigate();

  const { legacyMenu } = useSelector((state) => state.tenantSettings);

  const {
    checkRequiredPermissions,
    coreApiUrl,
    setCookies,
    checkRequiredAccess,
    // collapsed,setCollapsed
  } = useContext(AuthContext);

  const getFilteredMenuItems = (item) =>
    checkRequiredPermissions(item.requiredPermissions) &&
    (!item.path || checkRequiredAccess(item.path, true)) &&
    (!item.children || item.children.length > 0);

  const hasV4EnterpriseLicense = () => {
    const isEnabled =
      checkRequiredAccess("v4Enterprise") &&
      checkRequiredPermissions([
        permissionsMap.TENANT_OWNER,
        permissionsMap.TENANT_MANAGER,
      ]);
    return isEnabled;
  };

  const showEnterpriseButton =
    shouldShowV4Enterprise() && hasV4EnterpriseLicense();

  const handleGoToEnterpriseAnalytics = () => {
    openEnterpriseAppInNewTab();
    setDropdownVisible(false);
  };

  const legacyMenuGroup = [
    {
      icon: <Dashboard />,
      label: "Dashboard",
      path: DASHBOARD,
      key: "dashboard",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      icon: <MdOutlineIntegrationInstructions className="w-[18px] mr-3.5" />,
      label: "Integrations",
      path: INTEGRATION_DASHBOARD,
      key: "integrations-dashboard",
      requiredPermissions: [permissionsMap.TENANT_OWNER],
    },
    {
      path: ATTENDANCE,
      icon: <Attendance />,
      label: "Attendance",
      key: "attendance",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      icon: <Realtime />,
      label: "Real Time",
      key: "realtime",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
      children: [
        {
          path: LIVESTREAM,
          icon: <Livestream viewBox="0 0 24 24" />,
          label: "Livestream",
          key: "livestream",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: FIELD,
          icon: <TbLiveView className="w-[18px] mr-3.5" />,
          key: "field",
          label: "Field",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
      ].filter(getFilteredMenuItems),
    },
    {
      icon: <Analytics />,
      label: "Analytics",
      key: "analytics",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
      children: [
        {
          path: TIMELINE,
          icon: <Timeline viewBox="0 0 24 24" />,
          label: "Timeline",
          key: "timeline",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: ACTIVITY,
          icon: <Activity viewBox="0 0 24 24" />,
          label: "Activity",
          key: "activity",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: PRODUCTIVITY,
          icon: <Productivity viewBox="0 0 24 24" />,
          label: "Productivity",
          key: "productivity",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: GOALS,
          icon: <Goals viewBox="0 0 24 24" />,
          label: "Goals",
          key: "goals",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: SCREENSHOT,
          icon: <MdScreenshotMonitor className="w-[18px] mr-3.5" />,
          label: "Screenshots",
          key: "screenshots",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: APPLICATION,
          icon: <Application viewBox="0 0 24 24" />,
          label: "Apps & URLs",
          key: "app&url",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
        {
          path: WELLNESS,
          icon: <Wellness viewBox="0 0 24 24" />,
          label: "Wellness",
          key: "wellness",
          requiredPermissions: [
            permissionsMap.TENANT_MANAGER,
            permissionsMap.TENANT_OWNER,
          ],
        },
      ].filter(getFilteredMenuItems),
    },
    {
      path: DEVICE360,
      icon: <DeviceInfoIcon viewBox="0 0 24 24" />,
      key: "device360",
      label: "Devices",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      path: USB_DETECTION,
      icon: <UsbDetection />,
      key: "usbdetection",
      label: "USB Detection",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      path: MANUALTIME,
      icon: <ManualTime />,
      label: "Manual Time",
      key: "leaves",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,

        permissionsMap.TENANT_USER,
        permissionsMap.IAM_MANAGE_ALL,
      ],
    },
    {
      path: ALERTS,
      icon: <Alerts />,
      label: "Alerts",
      key: "alerts",
      requiredPermissions: [permissionsMap.TENANT_OWNER],
    },

    {
      path: REPORTS,
      icon: <Reports />,
      label: "Reports",
      key: "reports",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      path: TIMESHEET,
      icon: <Timesheet />,
      label: (
        <div className="flex items-center space-x-2">
          <span>Timesheet</span>
          {/* <Badge
            count={"Trial"} // This is where the badge count goes, replace 'Trial' with your dynamic value
            style={{ backgroundColor: "gold", color: "#000" }} // Gold badge with black text
            className="text-sm"
          /> */}
          <Badge count="Trial" size="small" color="gold" />
        </div>
      ),
    },
    {
      path: PROJECTS,
      icon: <Project />,
      label: "Projects",
      key: "project",
      requiredPermissions: [permissionsMap.TENANT_USER],
    },

    {
      path: LEAVE_MANAGEMENT,
      icon: <Leave />,
      label: "Leaves",
      key: "leaves",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
        permissionsMap.TENANT_USER,
        permissionsMap.IAM_MANAGE_ALL,
      ],
    },
    {
      path: NOTEBOOK,
      icon: <GiNotebook size={20} />,
      label: "Notebook",
      key: "notebook",
      requiredPermissions: [permissionsMap.TENANT_USER],
    },
    {
      path: ME,
      icon: <AiOutlineUser size={20} />,
      label: !checkRequiredPermissions([
        permissionsMap.TENANT_OWNER,
        permissionsMap.TENANT_MANAGER,
      ])
        ? "Me"
        : "User Detail",
      key: "me",
      requiredPermissions: [permissionsMap.TENANT_USER],
    },
    {
      path: SETTINGS,
      icon: <SettingsM />,
      label: "Settings",
      key: "settings",
      requiredPermissions: [
        permissionsMap.TENANT_OWNER,
        permissionsMap.IAM_MANAGE_ALL,
        permissionsMap.IAM_TEAM_MANAGE,
      ],
    },
  ].filter(getFilteredMenuItems);

  const pathArray = [
    DASHBOARD,
    ATTENDANCE,
    LIVESTREAM,
    FIELD,
    TIMELINE,
    ACTIVITY,
    PRODUCTIVITY,
    GOALS,
    SCREENSHOT,
    APPLICATION,
    WELLNESS,
    DEVICE360,
    MANUALTIME,
    ALERTS,
    REPORTS,
    TIMESHEET,
    PROJECTS,
    LEAVE_MANAGEMENT,
    NOTEBOOK,
    ME,
    SETTINGS,
    USB_DETECTION,
  ];

  const menuGroups = {
    productivity: [
      {
        icon: <Dashboard />,
        label: "Dashboard",
        path: DASHBOARD,
        key: "dashboard",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
      },
      {
        icon: <MdOutlineIntegrationInstructions className="w-[18px] mr-3.5" />,
        label: "Integrations",
        path: INTEGRATION_DASHBOARD,
        key: "integrations-dashboard",
        requiredPermissions: [permissionsMap.TENANT_OWNER],
      },
      {
        icon: <Realtime />,
        label: "Real Time",
        key: "realtime",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
        children: [
          {
            path: LIVESTREAM,
            icon: <Livestream viewBox="0 0 24 24" />,
            label: "Livestream",
            key: "livestream",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: FIELD,
            icon: <TbLiveView className="w-[18px] mr-3.5" />,
            key: "field",
            label: "Field",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
        ].filter(getFilteredMenuItems),
      },
      {
        icon: <Analytics />,
        label: "Analytics",
        key: "analytics",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
        children: [
          {
            path: TIMELINE,
            icon: <Timeline viewBox="0 0 24 24" />,
            label: "Timeline",
            key: "timeline",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: ACTIVITY,
            icon: <Activity viewBox="0 0 24 24" />,
            label: "Activity",
            key: "activity",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: PRODUCTIVITY,
            icon: <Productivity viewBox="0 0 24 24" />,
            label: "Productivity",
            key: "productivity",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: GOALS,
            icon: <Goals viewBox="0 0 24 24" />,
            label: "Goals",
            key: "goals",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: SCREENSHOT,
            icon: <MdScreenshotMonitor className="w-[18px] mr-3.5" />,
            label: "Screenshots",
            key: "screenshots",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: APPLICATION,
            icon: <Application viewBox="0 0 24 24" />,
            label: "Apps & URLs",
            key: "app&url",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
          {
            path: WELLNESS,
            icon: <Wellness viewBox="0 0 24 24" />,
            label: "Wellness",
            key: "wellness",
            requiredPermissions: [
              permissionsMap.TENANT_MANAGER,
              permissionsMap.TENANT_OWNER,
            ],
          },
        ].filter(getFilteredMenuItems),
      },
      {
        path: MANUALTIME,
        icon: <ManualTime />,
        label: "Manual Time",
        key: "leaves",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
          permissionsMap.TENANT_USER,
          permissionsMap.IAM_MANAGE_ALL,
        ],
      },
      {
        path: DEVICE360,
        icon: <DeviceInfoIcon viewBox="0 0 24 24" />,
        key: "device360",
        label: "Devices",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
      },
      {
        path: USB_DETECTION,
        icon: <UsbDetection />,
        key: "usbdetection",
        label: "USB Detection",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
      },
      {
        path: ALERTS,
        icon: <Alerts />,
        label: "Alerts",
        key: "alerts",
        requiredPermissions: [permissionsMap.TENANT_OWNER],
      },
    ].filter(getFilteredMenuItems),
    project: [
      {
        path: PROJECTS,
        icon: <Project />,
        label: "Projects",
        key: "project",
        requiredPermissions: [permissionsMap.TENANT_USER],
      },
      {
        path: TIMESHEET,
        icon: <Timesheet />,
        label: (
          <div className="flex items-center space-x-2">
            <span>Timesheet</span>
            {/* <Badge
              count={"Trial"} // This is where the badge count goes, replace 'Trial' with your dynamic value
              style={{ backgroundColor: "gold", color: "#000" }} // Gold badge with black text
              className="text-sm"
            /> */}
            <Badge count="Trial" size="small" color="gold" />
          </div>
        ),
      },

      {
        path: NOTEBOOK,
        icon: <GiNotebook size={20} />,
        label: "Notebook",
        key: "notebook",
        requiredPermissions: [permissionsMap.TENANT_USER],
      },
    ].filter(getFilteredMenuItems),
    hr: [
      {
        path: ATTENDANCE,
        icon: <Attendance />,
        label: "Attendance",
        key: "attendance",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
        ],
      },
      {
        path: LEAVE_MANAGEMENT,
        icon: <Leave />,
        label: "Leaves",
        key: "leaves",
        requiredPermissions: [
          permissionsMap.TENANT_MANAGER,
          permissionsMap.TENANT_OWNER,
          permissionsMap.TENANT_USER,
          permissionsMap.IAM_MANAGE_ALL,
        ],
      },
    ].filter(getFilteredMenuItems),
  };

  const clickSilder = (val) => {
    setCollapsed(val);
    silderBtn(val);
  };

  const onLogoError = () => {
    setCookies();
    if (logoRef.current) {
      logoRef.current.src = "/images/logo-light.png";
    }
  };

  const renderMenuItems = (menuItems) =>
    menuItems.map(({ label, icon, path, children, key }) =>
      children ? (
        <Menu.SubMenu
          key={key}
          title={label}
          icon={icon}
          popupClassName="submenu-item"
        >
          {renderMenuItems(children)}
        </Menu.SubMenu>
      ) : (
        <Menu.Item key={path} icon={icon}>
          <NavLink to={path}>{label}</NavLink>
        </Menu.Item>
      )
    );

  const toggleMenu = (menu) => {
    setActiveMenu(menu);
    localStorage.setItem("activeMenu", menu);
    setDropdownVisible(false);
    const flattenMenu = flattenTree(menuGroups[menu]).filter(
      (item) => !!item.path
    );
    const flattenCommonMenuItems = flattenTree(commonMenuItems).filter(
      (item) => !!item.path
    );
    const defaultRoute =
      flattenMenu.length > 0
        ? flattenMenu[0]?.path
        : flattenCommonMenuItems.length > 0
        ? flattenCommonMenuItems[0]?.path
        : null;
    if (defaultRoute) {
      navigate(defaultRoute);
    }
  };

  useEffect(() => {
    const currentPath = `/${window.location.pathname.split("/")[1]}`;
    if (!pathArray.includes(currentPath) && currentPath !== "/") {
      return;
    }
    if (!legacyMenu) {
      const flattenCommonMenuItems = flattenTree(commonMenuItems).filter(
        (item) => !!item.path
      );
      if (
        flattenCommonMenuItems.map((item) => item.path).includes(currentPath)
      ) {
        return;
      }

      let pathFound = false;
      for (const group in menuGroups) {
        if (
          flattenTree(menuGroups[group])
            .filter((item) => !!item.path)
            .some((item) => item.path === currentPath)
        ) {
          pathFound = true;

          const flattenActiveMenu = flattenTree(
            menuGroups[activeMenu] || []
          ).filter((item) => !!item.path);
          if (currentPath === "/dashboard" && flattenActiveMenu.length > 0) {
            const activeMenuPath = flattenActiveMenu[0].path;
            if (activeMenuPath && activeMenuPath !== currentPath) {
              navigate(activeMenuPath); // Always navigate to the first item of the active menu
            }
            return;
          }
          if (activeMenu !== group) {
            setActiveMenu(group);
            localStorage.setItem("activeMenu", group);
          }
          break;
        }
      }

      if (!pathFound) {
        const flattenActiveMenu = flattenTree(
          menuGroups[activeMenu] || []
        ).filter((item) => !!item.path);
        const flattenMergedMenu = flattenTree(mergedMenu).filter(
          (item) => !!item.path
        );
        const newPath =
          flattenActiveMenu.length > 0
            ? flattenActiveMenu[0].path
            : flattenMergedMenu.length > 0
            ? flattenMergedMenu[0].path
            : null;
        if (newPath) {
          navigate(newPath);
        }
      }
    } else {
      const flattenMergedMenu = flattenTree(mergedMenu).filter(
        (item) => !!item.path
      );
      if (!flattenMergedMenu.map((item) => item.path).includes(currentPath)) {
        const newPath =
          flattenMergedMenu.length > 0 ? flattenMergedMenu[0].path : null;
        if (newPath) {
          navigate(newPath);
        }
      }
    }
  }, [location.pathname, legacyMenu]);

  const commonMenuItems = [
    {
      path: REPORTS,
      icon: <Reports />,
      label: "Reports",
      key: "reports",
      requiredPermissions: [
        permissionsMap.TENANT_MANAGER,
        permissionsMap.TENANT_OWNER,
      ],
    },
    {
      path: ME,
      icon: <AiOutlineUser size={20} />,
      label: !checkRequiredPermissions([
        permissionsMap.TENANT_OWNER,
        permissionsMap.TENANT_MANAGER,
      ])
        ? "Me"
        : "User Detail",
      key: "me",
      requiredPermissions: [permissionsMap.TENANT_USER],
    },
    {
      path: SETTINGS,
      icon: <SettingsM />,
      label: "Settings",
      key: "settings",
      requiredPermissions: [
        permissionsMap.TENANT_OWNER,
        permissionsMap.IAM_MANAGE_ALL,
      ],
    },
  ].filter(getFilteredMenuItems);

  const mergedMenu = [
    ...menuGroups.productivity,
    ...menuGroups.project,
    ...menuGroups.hr,
    ...commonMenuItems,
  ];

  return (
    <Sider
      trigger={null}
      collapsed={collapsed}
      className="aside_slid"
      width="220px"
      collapsible
    >
      <div
        className="sider-logo mainlogo"
        style={{
          "background-color": branding?.theme?.sidebar_background || "#0e082e",
          cursor: "pointer",
        }}
      >
        {checkRequiredPermissions([
          permissionsMap.TENANT_OWNER,
          permissionsMap.TENANT_MANAGER,
        ]) &&
          !legacyMenu &&
          !collapsed && (
          <Dropdown
            open={dropdownVisible}
            onOpenChange={setDropdownVisible}
            trigger={['click']}
            placement="bottomLeft"
            align={{ offset: [0, 25] }}
            dropdownRender={() => (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[320px]">
                <div className="py-1">
                  {showEnterpriseButton && (
                    <>
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Apps & Portals</p>
                      </div>
                      <button
                        onClick={handleGoToEnterpriseAnalytics}
                        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                          <IoDiamondOutline className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-gray-900">Enterprise Analytics</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Advanced analytics and business insights</p>
                        </div>
                      </button>
                    </>
                  )}
                  <div className={showEnterpriseButton ? "px-4 py-2 border-t border-gray-200" : "px-4 py-2"}>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Quick Access</p>
                  </div>
                  <div className="py-1">
                          <button
                            onClick={() => toggleMenu('productivity')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                          >
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm">
                              <img src={Productivitysuite} alt="Productivity" width={20} style={{ filter: 'brightness(0) invert(1)' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-sm text-gray-900">Productivity Suite</span>
                              {activeMenu === 'productivity' && (
                                <span className="ml-2 text-xs text-purple-600 font-medium">Active</span>
                              )}
                            </div>
                          </button>
                          
                          <button
                            onClick={() => toggleMenu('project')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                          >
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-sm">
                              <img src={Projectsuite} alt="Project" width={20} style={{ filter: 'brightness(0) invert(1)' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-sm text-gray-900">Project Suite</span>
                              {activeMenu === 'project' && (
                                <span className="ml-2 text-xs text-yellow-600 font-medium">Active</span>
                              )}
                            </div>
                          </button>
                          
                          <button
                            onClick={() => toggleMenu('hr')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                          >
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center shadow-sm">
                              <img src={HRsuite} alt="HR" width={20} style={{ filter: 'brightness(0) invert(1)' }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-sm text-gray-900">HR Suite</span>
                              {activeMenu === 'hr' && (
                                <span className="ml-2 text-xs text-pink-600 font-medium">Active</span>
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
              </div>
            )}
          >
            <button
              className="flex items-center justify-center text-white bg-transparent hover:bg-[rgba(255,255,255,0.1)] rounded transition-all duration-200 cursor-pointer border-0"
              style={{ 
                padding: '0px',
                marginTop: '6px',
              }}
              aria-label="Open Apps Menu"
            >
              <BsFillGridFill style={{ fontSize: '18px' }} />
            </button>
          </Dropdown>
        )}

        <img
          className="max-h-12 w-100%"
          ref={logoRef}
          src={
            checkRequiredAccess("whiteLabelling")
              ? `${getLogoUrl(coreApiUrl)}`
              : "/images/logo-light.png"
          }
          onError={onLogoError}
        />

        {collapsed ? (
          // <RiMenuUnfoldFill
          //   style={{
          //     color: "white",
          //     float: "right",
          //     paddingLeft: "5px",
          //     fontSize: "25px",
          //   }}
          //   onClick={() => clickSilder(!collapsed)}
          // />

          <LuArrowRightFromLine
            style={{
              color: "white",
              float: "right",
              paddingLeft: "5px",
              fontSize: "20px",
              color: "#5A5480",
            }}
            onClick={() => clickSilder(!collapsed)}
          />
        ) : (
          <LuArrowLeftToLine
            style={{
              color: "white",
              float: "right",
              margin: "0 -20px 0 0",
              fontSize: "20px",
              color: "#5A5480",
            }}
            onClick={() => clickSilder(!collapsed)}
          />
        )}
      </div>

      <div className="side_content">
        {checkRequiredPermissions([
          permissionsMap.TENANT_OWNER,
          permissionsMap.TENANT_MANAGER,
        ]) &&
          !legacyMenu &&
          !collapsed && (
            <p className="text-[#7667C0] pt-2 pb-4 pl-2 pr-2 text-xs">
              {activeMenu === "hr"
                ? "HR suite"
                : `${activeMenu[0].toUpperCase()}${activeMenu.substring(
                    1
                  )} suite`}
            </p>
          )}

        <Menu
          mode="inline"
          selectable={true}
          className="menu-item"
          selectedKeys={[]}
        >
          {legacyMenu
            ? renderMenuItems(legacyMenuGroup)
            : checkRequiredPermissions([
                permissionsMap.TENANT_OWNER,
                permissionsMap.TENANT_MANAGER,
              ])
            ? renderMenuItems([...menuGroups[activeMenu], ...commonMenuItems])
            : renderMenuItems(mergedMenu)}
        </Menu>
      </div>
    </Sider>
  );
};

Sidebar.propTypes = {
  silderBtn: PropTypes.func,
};

export default Sidebar;